import {
  Search,
  ShoppingBag,
  Heart,
  Menu,
  X,
  ChevronDown,
  Phone,
} from 'lucide-react';
import { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import {
  MAIN_NAV,
  MEGA_MENU_CATEGORIES,
  QUICK_LINKS,
} from '../constants/navigation';
import { useBodyLock } from '../hooks/useBodyLock';
import { writeLangCookie, type Lang } from '../lib/langPrefs';
import { translatePath } from '../lib/slugPairs';
import { useCartStore } from '../store/cartStore';
import { useWishlistStore } from '../store/wishlistStore';

import IconButton from './ui/IconButton';

// SearchModal pulls in framer-motion. Lazy-load it so vendor-motion isn't on
// the critical path for every page — only when the user actually opens search.
const SearchModal = lazy(() => import('./search/SearchModal'));

export default function Header() {
  const { t, i18n } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();
  const { items } = useCartStore();
  const { items: wishlistItems } = useWishlistStore();

  const isBulgarian = i18n.language === 'bg';
  const lang: Lang = isBulgarian ? 'bg' : 'en';
  const prefix = `/${lang}`;
  // Localized leaf slugs used for top-level cart / wishlist routes. The Header
  // composes URLs by hand here (rather than via translatePath) because these
  // destinations are not derived from the current pathname.
  const wishlistSlug = isBulgarian ? 'любими' : 'wishlist'; // i18n-ignore: route slug
  const cartSlug = isBulgarian ? 'количка' : 'cart'; // i18n-ignore: route slug

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // Keep SearchModal mounted after first open so AnimatePresence can play its
  // exit animation. Before the first open, the lazy chunk hasn't been fetched.
  const [hasOpenedSearch, setHasOpenedSearch] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);

  const openSearch = () => {
    setIsSearchOpen(true);
    setHasOpenedSearch(true);
  };

  const megaMenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const cartItemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const wishlistCount = wishlistItems.length;

  useBodyLock(isMobileMenuOpen);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
  }, [location.pathname]);

  const handleMegaMenuEnter = (menuKey: string) => {
    if (megaMenuTimeoutRef.current) {
      clearTimeout(megaMenuTimeoutRef.current);
    }
    setActiveMegaMenu(menuKey);
  };

  const handleMegaMenuLeave = () => {
    megaMenuTimeoutRef.current = setTimeout(() => {
      setActiveMegaMenu(null);
    }, 300);
  };

  const toggleLanguage = () => {
    const currentLang: Lang = isBulgarian ? 'bg' : 'en';
    const nextLang: Lang = isBulgarian ? 'en' : 'bg';

    // Compute the equivalent URL in the other language.
    const currentPath = location.pathname + location.search + location.hash;
    let nextPath: string;
    try {
      nextPath = translatePath(currentPath, currentLang, nextLang);
    } catch {
      // Path doesn't have the expected prefix — shouldn't happen in normal flow,
      // but fall back to the language's home.
      nextPath = `/${nextLang}/`;
    }

    writeLangCookie(nextLang);
    void i18n.changeLanguage(nextLang);
    navigate(nextPath, { replace: false });
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-header bg-ivory border-b border-line">
        {/* Main row — logo · centered nav · actions */}
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-[auto_1fr_auto] items-center h-16 lg:h-20 gap-2 sm:gap-3 lg:gap-6">
            {/* Logo */}
            <Link
              to={prefix}
              className="flex flex-col leading-none group"
              aria-label={t('header.logoAriaLabel')}
            >
              <span className="font-display text-[28px] lg:text-[32px] text-ink tracking-tight">
                Gul<span className="italic text-rust">Art</span>
              </span>
              <span className="hidden sm:block text-[9px] uppercase tracking-[0.22em] text-ink-3 mt-0.5">
                {t('header.tagline')}
              </span>
            </Link>

            {/* Centered desktop nav */}
            <nav className="hidden lg:flex items-center justify-center gap-8">
              {MAIN_NAV.map((link) => {
                const isActive = location.pathname === `${prefix}${link.href}`;
                return (
                  <div
                    key={link.href}
                    className="relative"
                    onMouseEnter={() =>
                      link.megaMenu && handleMegaMenuEnter(link.labelKey)
                    }
                    onMouseLeave={handleMegaMenuLeave}
                  >
                    <Link
                      to={`${prefix}${link.href}`}
                      className={`flex items-center gap-1 py-2 text-[14px] font-medium transition-colors whitespace-nowrap ${
                        isActive ? 'text-ink' : 'text-ink-2 hover:text-ink'
                      }`}
                    >
                      <span className="relative">
                        {t(link.labelKey)}
                        <span
                          className={`absolute -bottom-1 left-0 right-0 h-px transition-all ${
                            isActive ? 'bg-rust' : 'bg-transparent'
                          }`}
                        />
                      </span>
                      {link.megaMenu && (
                        <ChevronDown
                          className={`w-3.5 h-3.5 transition-transform duration-200 ${
                            activeMegaMenu === link.labelKey ? 'rotate-180' : ''
                          }`}
                        />
                      )}
                    </Link>

                    {link.megaMenu && (
                      <div
                        className={`absolute top-full left-1/2 -translate-x-1/2 w-[min(820px,calc(100vw-2rem))] bg-ivory border border-line shadow-soft-lg z-modal transition-all duration-200 mt-2 ${
                          activeMegaMenu === link.labelKey
                            ? 'opacity-100 visible translate-y-0 pointer-events-auto'
                            : 'opacity-0 invisible -translate-y-1 pointer-events-none'
                        }`}
                        onMouseEnter={() => handleMegaMenuEnter(link.labelKey)}
                        onMouseLeave={handleMegaMenuLeave}
                      >
                        <div className="p-8">
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                            {MEGA_MENU_CATEGORIES.map((category) => (
                              <div key={category.titleKey}>
                                <h3 className="font-display text-[20px] text-ink mb-4 leading-none">
                                  {t(category.titleKey)}
                                </h3>
                                <ul className="space-y-2.5">
                                  {category.items.map((item) => (
                                    <li key={item.href}>
                                      <Link
                                        to={`${prefix}${item.href}`}
                                        className="text-[13px] text-ink-2 hover:text-rust transition-colors"
                                      >
                                        {t(item.labelKey)}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                          <div className="mt-8 pt-6 border-t border-line-soft flex items-center gap-3 flex-wrap">
                            {QUICK_LINKS.map((ql) => (
                              <Link
                                key={ql.href}
                                to={`${prefix}${ql.href}`}
                                className="inline-flex items-center gap-2 px-3.5 py-1.5 border border-line hover:border-ink text-[12px] text-ink-2 hover:text-ink transition-colors"
                              >
                                <span>{ql.icon}</span>
                                {t(ql.labelKey)}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-1.5 lg:gap-2 justify-end ml-auto">
              {/* Search pill — desktop only */}
              <button
                onClick={openSearch}
                className="hidden lg:flex items-center gap-2 min-w-[200px] h-10 px-4 rounded-full border border-line hover:border-ink text-[13px] text-ink-3 hover:text-ink transition-colors"
              >
                <Search className="w-4 h-4" />
                <span>{t('header.searchPlaceholder')}</span>
              </button>

              {/* Mobile search */}
              <IconButton
                onClick={openSearch}
                className="lg:hidden"
                aria-label={t('header.searchAriaLabel')}
              >
                <Search className="w-5 h-5" />
              </IconButton>

              {/* Wishlist */}
              <Link
                to={`${prefix}/${wishlistSlug}`}
                className="hidden sm:inline-flex w-11 h-11 md:w-9 md:h-9 items-center justify-center rounded-full text-ink-2 hover:bg-ivory-2 transition-colors touch-manipulation relative"
                aria-label={t('header.wishlistAriaLabel')}
              >
                <Heart className="w-5 h-5" />
                {wishlistCount > 0 && (
                  <span className="absolute top-0 right-0 min-w-[16px] h-[16px] px-1 bg-rust text-ivory text-[10px] font-medium rounded-full flex items-center justify-center">
                    {wishlistCount > 9 ? '9+' : wishlistCount}
                  </span>
                )}
              </Link>

              {/* Language toggle — desktop */}
              <button
                onClick={toggleLanguage}
                className="hidden lg:block px-2 py-1 text-[12px] uppercase tracking-[0.14em] text-ink-3 hover:text-ink transition-colors"
                aria-label={t('header.langSwitcherAriaLabel')}
              >
                {isBulgarian ? 'BG / EN' : 'EN / BG'}
              </button>

              {/* Cart */}
              <Link
                to={`${prefix}/${cartSlug}`}
                className="inline-flex w-11 h-11 md:w-9 md:h-9 items-center justify-center rounded-full text-ink-2 hover:bg-ivory-2 transition-colors touch-manipulation relative"
                aria-label={t('header.cartAriaLabel')}
              >
                <ShoppingBag className="w-5 h-5" />
                {cartItemCount > 0 && (
                  <span className="absolute top-0 right-0 min-w-[16px] h-[16px] px-1 bg-rust text-ivory text-[10px] font-medium rounded-full flex items-center justify-center">
                    {cartItemCount > 9 ? '9+' : cartItemCount}
                  </span>
                )}
              </Link>

              {/* Mobile menu toggle */}
              <IconButton
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden"
                aria-label={
                  isMobileMenuOpen
                    ? t('header.menuCloseAriaLabel')
                    : t('header.menuOpenAriaLabel')
                }
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Menu className="w-5 h-5" />
                )}
              </IconButton>
            </div>
          </div>
        </div>
      </header>

      {hasOpenedSearch && (
        <Suspense fallback={null}>
          <SearchModal
            isOpen={isSearchOpen}
            onClose={() => setIsSearchOpen(false)}
          />
        </Suspense>
      )}

      {/* Mobile menu drawer */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-x-0 bg-ivory animate-slide-in-right overflow-y-auto z-drawer"
          style={{
            top: 'var(--header-height)',
            height: 'calc(100dvh - var(--header-height))',
          }}
        >
          <div className="px-6 pt-6 pb-12">
            {/* Language toggle — mobile drawer */}
            <div className="mb-6 flex justify-end">
              <button
                onClick={toggleLanguage}
                className="px-2 py-1 text-[14px] uppercase tracking-[0.14em] text-ink-3 hover:text-ink transition-colors"
                aria-label={t('header.langSwitcherAriaLabel')}
              >
                {isBulgarian ? 'BG / EN' : 'EN / BG'}
              </button>
            </div>

            {/* Editorial-style nav list */}
            <ul className="divide-y divide-line">
              {MAIN_NAV.map((link) => (
                <li key={link.href}>
                  <Link
                    to={`${prefix}${link.href}`}
                    onClick={() => !link.megaMenu && setIsMobileMenuOpen(false)}
                    className="flex items-baseline justify-between py-5 group"
                  >
                    <span className="font-display text-[28px] text-ink leading-none">
                      {t(link.labelKey)}
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.2em] text-ink-3 group-hover:text-rust transition-colors">
                      →
                    </span>
                  </Link>
                  {link.megaMenu && (
                    <div className="pb-5 space-y-4">
                      {MEGA_MENU_CATEGORIES.map((category) => (
                        <div key={category.titleKey}>
                          <div className="text-[10px] uppercase tracking-[0.2em] text-ink-3 mb-2">
                            {t(category.titleKey)}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                            {category.items.map((item) => (
                              <Link
                                key={item.href}
                                to={`${prefix}${item.href}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="text-[14px] text-ink-2 hover:text-rust"
                              >
                                {t(item.labelKey)}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>

            {/* Contact */}
            <div className="mt-10 pt-8 border-t border-line">
              <a
                href="tel:+359894488936"
                className="flex items-center gap-3 text-ink"
              >
                <Phone className="w-5 h-5 text-rust" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-ink-3">
                    {t('header.callUs')}
                  </div>
                  <div className="font-display text-[22px] leading-none mt-0.5">
                    +359 89 448 8936
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Spacer for fixed header: main row h-16 (mobile) / h-20 (lg+) */}
      <div style={{ height: 'var(--header-height)' }} aria-hidden="true" />
    </>
  );
}
